import { Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import { ButtonModule } from "primeng/button";
import { BottomSheetComponent } from "../bottom-sheet/bottom-sheet.component";
import { SyncStatusSheetComponent } from "./sync-status-drawer/sync-status-drawer.component";
import SyncManager from "../../../core/sync/sync-manager";
import SyncStatusService, { SyncStatus } from "../../../core/sync/sync-status-service";

const FAILED_PULSE_THRESHOLD = 5;

@Component({
  selector: "online-indicator",
  standalone: true,
  imports: [CommonModule, ButtonModule, BottomSheetComponent, SyncStatusSheetComponent],
  templateUrl: "./online-indicator.component.html",
  styleUrl: "./online-indicator.component.scss"
})
export class OnlineIndicatorComponent {
  onlineStatus = true; // fallback
  syncStatusEnum = SyncStatus;
  isBottomSheetOpen = false;
  lastSynced = signal<Date | null>(null);

  private readonly network = inject(NetworkStatusService);
  private readonly syncManager = inject(SyncManager);
  private readonly syncStatusService = inject(SyncStatusService);
  private readonly destroyRef = inject(DestroyRef);
  syncStatus: SyncStatus = this.syncStatusService.getSyncStatus();

  readonly online$ = this.network.online$;
  private didStatusChange = false;

  async ngOnInit() {
    this.lastSynced.set(await this.syncManager.getLastSyncDate() ?? new Date());

    this.online$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
      this.onlineStatus = val;
    });

    this.syncStatusService.syncStatusChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async status => {
      this.syncStatus = status;
      this.didStatusChange = true;
      this.lastSynced.set(await this.syncManager.getLastSyncDate() ?? new Date());
    });
  }

  showIndicatorBottomSheet() {
    this.isBottomSheetOpen = true;
  }

  getIconClass(): string {
    if (!this.onlineStatus) {
      return "icon-offline breathe";
    } else if (this.syncStatus === this.syncStatusEnum.InProgress) {
      return "icon-in-progress spin-cw";
    } else if (this.syncStatus === this.syncStatusEnum.Complete) {
      return "icon-complete";
    } else if (this.syncStatus === this.syncStatusEnum.Failed) {
      return "icon-failed breathe";
    } else if (this.syncStatus === this.syncStatusEnum.Incomplete) {
      return "icon-incomplete breathe";
    }
    return "";
  }

  getAnimationClass(): string {
    if (this.onlineStatus && this.didStatusChange) {
      if (this.syncStatus === this.syncStatusEnum.Failed && this.syncStatusService.getSyncStatusQueueLength() >= FAILED_PULSE_THRESHOLD) {
        return "failed-pulse pulse-infinite";
      }
      if (this.syncStatus === this.syncStatusEnum.Failed) {
        return "failed-pulse";
      }
      if (this.syncStatus === this.syncStatusEnum.Complete) {
        return "success-pulse pulse";
      }
    }

    return "";
  }
}
